import {
  QueryKey,
  UseMutationOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import {
  getFetchFn,
  getApiEndpoints,
  MutationRequestMethod,
  ResponseNormaliserFn,
  useGet,
  useInfiniteGet,
  useSet,
  useVariableUrlSet,
  type Header,
  CushlaGetQueryOptions,
  CushlaInfiniteQueryOptions,
} from '@common/api'
import {getApiRequestError, useCustomErrorMessage} from '@common/utils'
import {AuthError, GeneralPagedReponse, UserRole} from '@common/models'
import {useAccount, useMsal} from '@azure/msal-react'
import {InteractionRequiredAuthError} from '@azure/msal-common'
import {InteractionStatus} from '@azure/msal-browser'
import {useCallback, useContext} from 'react'

import {PractitionerContext} from '~/components/profile/PractitionerContext'
import {isDevelopment} from '~/utils'
import {getUserRoles} from '~/utils/simulateJWT'
import {b2cScopes, loginRequest} from '~/auth/AuthConfig'
import {
  NotificationContext,
  NotificationType,
} from '~/components/notifications/NotificationContext'

export const useApi = () => {
  const apiUrl = `${window.location.origin}/api/v1/`
  const localUserId = process.env.LOCAL_USER_ID
  const {accounts} = useMsal()

  // Get user roles to determine if we should include practitionerId
  const jwtClaims = accounts[0]?.idTokenClaims as
    | Record<string, string>
    | undefined
  const roles = getUserRoles(jwtClaims)
  const isPractitioner = roles.split(',').includes(UserRole.PRACTITIONER)

  // Only set practitionerId if user is actually a practitioner
  let practitionerId: string | undefined
  if (isPractitioner) {
    practitionerId = isDevelopment ? localUserId : accounts[0]?.localAccountId
  }

  return getApiEndpoints({
    apiUrl,
    practitionerId: practitionerId || '',
  })
}

export const useHeaders = (addPractitionerAuthHeaders = true) => {
  const localUserId = process.env.LOCAL_USER_ID
  const {selectedEmployment} = useContext(PractitionerContext)

  const {instance, accounts, inProgress} = useMsal()
  const account = useAccount(accounts[0] || {})

  return useCallback(async () => {
    if (!addPractitionerAuthHeaders) {
      return {}
    }

    const facilityHeader = selectedEmployment?.facilityId
      ? {'X-Facility-Id': selectedEmployment.facilityId}
      : {}

    if (isDevelopment) {
      return {Authorization: `Bearer ${localUserId}`, ...facilityHeader}
    }

    try {
      if (!account) {
        throw new Error('Account is missing or is not loaded yet.')
      }

      const {accessToken} = await instance.acquireTokenSilent({
        account,
        scopes: b2cScopes,
      })
      return {Authorization: `Bearer ${accessToken}`, ...facilityHeader}
    } catch (error) {
      // [CUSH-655] FIXME: Add error logging

      console.warn(error)

      if (error instanceof InteractionRequiredAuthError) {
        if (account && inProgress === InteractionStatus.None) {
          try {
            await instance.loginRedirect(loginRequest)
          } catch (err: unknown) {
            // [CUSH-655] FIXME: Add error logging

            console.error(err)
          }
        }
      }
      return Promise.reject(new AuthError())
    }
  }, [
    account,
    addPractitionerAuthHeaders,
    inProgress,
    instance,
    localUserId,
    selectedEmployment?.facilityId,
  ])
}

export const useErrorMessage = () => {
  const {addNotification} = useContext(NotificationContext)
  const getCustomErrorMessage = useCustomErrorMessage()

  return (error: Error) =>
    addNotification(
      getCustomErrorMessage(error) || error.message,
      NotificationType.Error,
    )
}

export const useWebGet = <T>(
  key: QueryKey,
  endpoint: URL,
  normaliser: (data: T) => T,
  options: CushlaGetQueryOptions<T> = {},
  addPractitionerAuthHeaders = true,
) => {
  const getHeader = useHeaders(addPractitionerAuthHeaders)

  return useGet(getHeader, key, endpoint, normaliser, {
    ...options,
  })
}

export const useWebInfiniteGet = <T extends GeneralPagedReponse>(
  key: QueryKey,
  endpoint: URL,
  normaliser: (data: T) => T,
  options?: CushlaInfiniteQueryOptions<T>,
) => {
  const getHeader = useHeaders()

  return useInfiniteGet(getHeader, key, endpoint, normaliser, {
    ...options,
  })
}

export const useWebSet = <RequestType, ResponseType = unknown>(
  key: QueryKey,
  endpoint: URL,
  method: MutationRequestMethod,
  normaliser: ResponseNormaliserFn<ResponseType>,
  invalidationKeys?: QueryKey[],
  options: UseMutationOptions<ResponseType, Error, RequestType> = {},
  addPractitionerAuthHeaders = true,
) => {
  const getHeader = useHeaders(addPractitionerAuthHeaders)
  const showErrorMessage = useErrorMessage()

  return useSet(
    getHeader,
    key,
    endpoint,
    method,
    normaliser,
    (() => {
      const finalOptions = {...options}
      // Only attach the generic error handler if the caller didn't provide its own
      if (!finalOptions.onError) {
        finalOptions.onError = showErrorMessage
      }
      return finalOptions
    })(),
    invalidationKeys,
  )
}

/**
 * PATCH/POST with multipart/form-data. Builds FormData from the payload; does not set Content-Type so the browser adds the multipart boundary.
 */
export const useWebSetFormData = <RequestType, ResponseType = unknown>(
  key: QueryKey,
  endpoint: URL,
  method: MutationRequestMethod,
  normaliser: ResponseNormaliserFn<ResponseType>,
  buildFormData: (payload: RequestType) => FormData,
  invalidationKeys: QueryKey[] = [],
  options: UseMutationOptions<ResponseType, Error, RequestType> = {},
  addPractitionerAuthHeaders = true,
) => {
  const getHeader = useHeaders(addPractitionerAuthHeaders)
  const client = useQueryClient()
  const showErrorMessage = useErrorMessage()

  return useMutation({
    mutationKey: key,
    mutationFn: async (payload: RequestType) => {
      const formData = buildFormData(payload)
      const headers = (await getHeader()) as Record<string, string>
      const response = await fetch(endpoint.href, {
        method,
        headers,
        body: formData,
      })
      if (!response.ok) {
        throw await getApiRequestError(response, endpoint.href, headers)
      }
      const json = await response.json()
      return normaliser ? normaliser(json) : (json as ResponseType)
    },
    ...options,
    onSuccess: async (data, variables, onMutateResult, context) => {
      await Promise.allSettled(
        invalidationKeys.map((k) => client.invalidateQueries({queryKey: k})),
      )
      await options.onSuccess?.(data, variables, onMutateResult, context)
    },
    onError: (error, variables, context, mutation) => {
      if (options.onError) {
        options.onError(error, variables, context, mutation)
      } else {
        showErrorMessage(error)
      }
    },
  })
}

export const useWebVariableUrlSet = <
  UrlParams extends Record<string, string | boolean | null>,
  RequestType extends Record<string, string | null>,
  ResponseType,
>(
  key: QueryKey,
  getEndpoint: (params: UrlParams) => URL,
  method: MutationRequestMethod,
  normaliser: ResponseNormaliserFn<ResponseType>,
  options?: UseMutationOptions<
    ResponseType,
    Error,
    {urlParams: UrlParams; requestBody?: RequestType}
  >,
  invalidationKeys?: QueryKey[],
  addPractitionerAuthHeaders = true,
) => {
  const getHeader = useHeaders(addPractitionerAuthHeaders)
  const showErrorMessage = useErrorMessage()

  return useVariableUrlSet(
    getHeader,
    key,
    getEndpoint,
    method,
    normaliser,
    {
      onError: showErrorMessage,
      ...(options ?? {}),
    },
    invalidationKeys,
  )
}

export const useFetchData = () => {
  const getHeader = useHeaders()

  return useCallback(
    <T>(endpoint: URL, normaliser: (data: T) => T) =>
      getFetchFn(getHeader, endpoint, normaliser)(),
    [getHeader],
  )
}

const fileNameFromContentDisposition = (
  header: string | null,
): string | undefined => {
  if (!header) return undefined
  const utf8 = header.match(/filename\*=UTF-8''([^;\n]+)/i)
  if (utf8?.[1]) {
    return decodeURIComponent(utf8[1])
  }
  const quoted = header.match(/filename="([^"]+)"/i)
  if (quoted?.[1]) {
    return quoted[1]
  }
  const simple = header.match(/filename=([^;\n]+)/i)
  if (simple?.[1]) {
    return simple[1].replace(/^["']|["']$/g, '')
  }
  return undefined
}

const saveBlobAsFile = (blob: Blob, filename: string) => {
  const blobUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = blobUrl
  a.download = filename
  a.rel = 'noopener noreferrer'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(blobUrl)
}

export type DownloadFileOptions = {
  fallbackToAnchorOnFailure?: boolean
}

export const downloadFile = async (
  endpoint: URL,
  getHeader: () => Promise<Header>,
  defaultFilename: string,
  options?: DownloadFileOptions,
): Promise<void> => {
  const fallbackToAnchorOnFailure = options?.fallbackToAnchorOnFailure ?? false

  const fetchBlobAndSave = async () => {
    const headers = await getHeader()
    const response = await fetch(endpoint.href, {
      mode: 'cors',
      cache: 'no-store',
      headers,
    })
    if (!response.ok) {
      throw new Error('Download failed')
    }
    const blob = await response.blob()
    if (fallbackToAnchorOnFailure && blob.size === 0) {
      throw new Error('empty body')
    }

    const fromHeader = fileNameFromContentDisposition(
      response.headers.get('Content-Disposition'),
    )
    const filename = fromHeader ?? defaultFilename
    saveBlobAsFile(blob, filename)
  }

  if (!fallbackToAnchorOnFailure) {
    await fetchBlobAndSave()
    return
  }

  try {
    await fetchBlobAndSave()
  } catch {
    const a = document.createElement('a')
    a.href = endpoint.href
    a.download = defaultFilename
    a.rel = 'noopener noreferrer'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }
}

export const useWebFileDownload = (
  key: QueryKey,
  getEndpoint: () => URL,
  defaultFilename: string,
  options?: UseMutationOptions<void, Error, void>,
  addPractitionerAuthHeaders = true,
) => {
  const getHeader = useHeaders(addPractitionerAuthHeaders)
  const showErrorMessage = useErrorMessage()

  const {mutate, isPending} = useMutation({
    mutationKey: key,
    mutationFn: async () => {
      const endpoint = getEndpoint()
      await downloadFile(endpoint, getHeader, defaultFilename)
    },
    ...options,
    onError: options?.onError || showErrorMessage,
  })

  return {mutate, isPending}
}
